import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { 
  CheckCircle2, 
  Shield, 
  Zap, 
  BookOpen, 
  Clock, 
  Sparkles,
  ArrowRight,
  Server,
  Lock,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import { submitLead } from '../services/api';

const LandingPage = () => {
  const [formData, setFormData] = useState({ firstName: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToForm = (e) => {
    e.preventDefault();
    const formSection = document.getElementById('get-started');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.email) {
      toast.error("Please provide both your name and email.");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const result = await submitLead(formData.firstName, formData.email);
      
      if (result.success) {
        toast.success(result.message || "Thank you! We'll be in touch soon.");
        setFormData({ firstName: '', email: '' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-6 lg:px-20 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-8 h-8 text-blue-400" />
            <span className="text-2xl font-bold text-white">OpenClaw</span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#benefits" className="text-gray-400 hover:text-white transition-colors">Benefits</a>
            <a href="#how-it-works" className="text-gray-400 hover:text-white transition-colors">How It Works</a>
            <a href="#get-started" className="text-gray-400 hover:text-white transition-colors">Get Started</a>
          </nav>
          <Button onClick={scrollToForm} className="hidden md:inline-flex btn-primary-custom">
            Get Started Free
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 hero-gradient"></div>
        <div className="absolute inset-0 opacity-30">
          <img 
            src="https://images.unsplash.com/photo-1672581437674-3186b17b405a" 
            alt="AI Automation"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black"></div>
        
        <div className="container mx-auto px-6 lg:px-20 relative z-10 pt-20">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="hero-headline text-white mb-6">
              Deploy AI Agents That Work For You — Without the Headaches
            </h1>
            <p className="hero-subheadline text-gray-200 mb-8 max-w-3xl mx-auto">
              We install and configure OpenClaw on our secure private VPS so you can automate repetitive business tasks instantly.
            </p>
            <Button onClick={scrollToForm} className="btn-primary-custom btn-large mb-12">
              Get Started Free
              <ArrowRight className="w-5 h-5" />
            </Button>

            <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              {[
                { icon: CheckCircle2, text: 'Free Installation Included' },
                { icon: Server, text: 'Runs on Our Secure Private VPS' },
                { icon: Zap, text: 'Built for Real Business Automation' }
              ].map((item, idx) => (
                <div key={idx} className="hero-bullet flex flex-col items-center gap-3 p-6 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10">
                  <item.icon className="w-8 h-8 text-blue-400" />
                  <span className="text-white text-sm font-medium text-center">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core Offer Section */}
      <section id="benefits" className="benefits-section py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-purple-950/20 to-black"></div>
        <div className="container mx-auto px-6 lg:px-20 relative z-10">
          <div className="text-center mb-16">
            <h2 className="section-headline text-white mb-4">
              Everything You Need to Harness OpenClaw
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              A complete AI automation solution designed for businesses ready to scale
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: CheckCircle2,
                title: 'Free Done-For-You Installation',
                description: 'No technical setup required. We handle everything from start to finish.',
                color: 'text-green-400'
              },
              {
                icon: Server,
                title: 'Hosted on Private VPS',
                description: 'Your AI agents run on our secure infrastructure. No local setup required.',
                color: 'text-blue-400'
              },
              {
                icon: Lock,
                title: 'No Security Risks',
                description: 'Keep your computer safe. Everything runs in our isolated environment.',
                color: 'text-purple-400'
              },
              {
                icon: BookOpen,
                title: 'Growing Library of Skills',
                description: 'Access pre-built automation skills and add custom ones as needed.',
                color: 'text-cyan-400'
              },
              {
                icon: Users,
                title: 'Training & Support Included',
                description: 'Get personalized assistance to maximize your automation potential.',
                color: 'text-pink-400'
              },
              {
                icon: Zap,
                title: 'Automate Business Tasks',
                description: 'Save hours daily by automating repetitive work across your business.',
                color: 'text-yellow-400'
              }
            ].map((benefit, idx) => (
              <Card key={idx} className="benefit-card p-8 bg-white/5 backdrop-blur-sm border-white/10 hover:border-blue-500/50 transition-all duration-500">
                <benefit.icon className={`w-12 h-12 ${benefit.color} mb-6`} />
                <h3 className="text-xl font-semibold text-white mb-3">{benefit.title}</h3>
                <p className="text-gray-400 leading-relaxed">{benefit.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Positioning Section */}
      <section className="positioning-section py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1667984390553-7f439e6ae401" 
            alt="Cloud Technology"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/80 via-blue-900/60 to-purple-900/80"></div>
        
        <div className="container mx-auto px-6 lg:px-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="section-headline text-white mb-8">
              This Is the Next Evolution of AI — And You're Early
            </h2>
            <p className="text-xl text-gray-200 leading-relaxed mb-8">
              OpenClaw represents a breakthrough in AI agent technology. Unlike traditional AI tools that just answer questions, 
              OpenClaw performs real tasks across your business—automatically. From data entry to customer follow-ups, 
              research to report generation, you're now at the leading edge of the AI agent revolution.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed">
              This technology saves hours every single day, increases productivity exponentially, and gives you leverage 
              that was impossible just months ago. The businesses adopting this now will have an insurmountable advantage.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works-section py-24 bg-black">
        <div className="container mx-auto px-6 lg:px-20">
          <div className="text-center mb-16">
            <h2 className="section-headline text-white mb-4">How It Works</h2>
            <p className="text-gray-400 text-lg">Get started in three simple steps</p>
          </div>

          <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-12">
            {[
              {
                step: '01',
                title: 'Request Access',
                description: 'Fill out the simple form below with your details. No credit card required.',
                icon: Users
              },
              {
                step: '02',
                title: 'We Install & Configure',
                description: 'Our team sets up OpenClaw on our secure private VPS tailored to your needs.',
                icon: Server
              },
              {
                step: '03',
                title: 'Start Automating',
                description: 'Begin delegating tasks to your AI agents immediately. Watch productivity soar.',
                icon: Zap
              }
            ].map((step, idx) => (
              <div key={idx} className="step-card text-center group">
                <div className="step-number text-7xl font-bold bg-gradient-to-r from-purple-500 to-blue-500 bg-clip-text text-transparent mb-6 group-hover:scale-110 transition-transform duration-300">
                  {step.step}
                </div>
                <div className="mb-6 flex justify-center">
                  <div className="p-4 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-lg border border-purple-500/30">
                    <step.icon className="w-10 h-10 text-blue-400" />
                  </div>
                </div>
                <h3 className="text-2xl font-semibold text-white mb-4">{step.title}</h3>
                <p className="text-gray-400 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Capture Section */}
      <section id="get-started" className="lead-capture-section py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-blue-900 to-purple-900"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff33_1px,transparent_1px),linear-gradient(to_bottom,#ffffff33_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
        </div>
        
        <div className="container mx-auto px-6 lg:px-20 relative z-10">
          <div className="max-w-2xl mx-auto">
            <Card className="p-12 bg-black/60 backdrop-blur-xl border-white/20 shadow-2xl">
              <div className="text-center mb-8">
                <h2 className="text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
                <p className="text-gray-300 text-lg">
                  Join the businesses already automating with OpenClaw
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-white font-medium mb-2">First Name *</label>
                  <Input
                    type="text"
                    placeholder="Enter your first name"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full bg-white/10 border-white/20 text-white placeholder:text-gray-500 h-14 text-lg focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Email Address *</label>
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-white/10 border-white/20 text-white placeholder:text-gray-500 h-14 text-lg focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full btn-primary-custom btn-large"
                >
                  {isSubmitting ? 'Sending...' : 'Send Me the Details'}
                  {!isSubmitting && <ArrowRight className="w-5 h-5" />}
                </Button>

                <p className="text-center text-sm text-gray-400">
                  No spam. Just information on how to get started.
                </p>
              </form>
            </Card>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="trust-section py-24 bg-black">
        <div className="container mx-auto px-6 lg:px-20">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="section-headline text-white mb-12">Built for Your Peace of Mind</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: Users,
                  title: 'No Technical Skills Required',
                  description: 'We handle all the complex setup and configuration for you'
                },
                {
                  icon: Lock,
                  title: 'Secure Environment',
                  description: 'Your data stays protected in our isolated, enterprise-grade infrastructure'
                },
                {
                  icon: Clock,
                  title: 'Personalized Assistance',
                  description: 'Get dedicated support to ensure you succeed with automation'
                }
              ].map((item, idx) => (
                <div key={idx} className="trust-card p-8 bg-white/5 border border-white/10 rounded-lg hover:border-blue-500/50 transition-all duration-300">
                  <item.icon className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                  <p className="text-gray-400">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-section py-16 bg-black border-t border-white/10">
        <div className="container mx-auto px-6 lg:px-20">
          <div className="text-center mb-8">
            <Button onClick={scrollToForm} className="btn-primary-custom btn-large mb-6">
              Get Started Free
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="flex items-center justify-center gap-2 mb-6">
            <Sparkles className="w-6 h-6 text-blue-400" />
            <span className="text-xl font-bold text-white">OpenClaw Deployment</span>
          </div>
          
          <p className="text-center text-gray-500 text-sm">
            © 2024 OpenClaw Deployment. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
